// ✅ 2) FULL updated DiscountCoupon.jsx (Copy/Paste)
// This version covers the FULL modal width — no inner maxWidth column.
// Everything is centered, but it USES the full available space.

import React, { useState } from "react";
import { useAppMutation } from "../../../../../services";
import parentServices from "../../../../../services/parent-services";
import Div from "@jumbo/shared/Div";
import { Alert, Button, Divider, TextField } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

const Row = ({ label, value }) => (
  <Div
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 0.35,
    }}
  >
    <Typography
      sx={{
        color: "rgb(102,102,102)",
        fontFamily: "Nunito, sans-serif",
        fontSize: "15px",
        fontWeight: 550,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        color: "rgb(0,36,101)",
        fontFamily: "Nunito, sans-serif",
        fontSize: "15px",
        fontWeight: 700,
      }}
    >
      {value}
    </Typography>
  </Div>
);

const DiscountCoupon = ({ plan, hide, next }) => {
  const { t } = useTranslation();
  const [values, setValues] = useState({ discountCode: "" });

  const {
    mutate: isEligibleForDiscount,
    isError,
    data,
    isSuccess,
    isLoading,
  } = useAppMutation(parentServices.isEligibleForDiscount);

  const [errMsg, setErrMsg] = React.useState("");

  const handleChange = (prop) => (event) => {
    values[prop] = event.target.value.toUpperCase();
    if (prop === "discountCode") {
      setValues({ ...values, applyDiscountCode: undefined });
    } else {
      setValues({ ...values });
    }
  };

  React.useEffect(() => {
    if (isSuccess) {
      if (data.eligible) {
        setValues({ ...values, applyDiscountCode: data });
      } else {
        setErrMsg(data.message);
      }
    } else if (isError) {
      setErrMsg("Discount code is not valid");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError]);

  const amountToPay = values.applyDiscountCode
    ? values.applyDiscountCode.chargedAmount
    : plan.amount;

  return (
    <Div
      sx={{
        width: "100%",
        backgroundColor: "#fff",
        borderRadius: "18px",
        position: "relative",
        boxSizing: "border-box",

        // ✅ make it fill the whole DialogContent
        px: { xs: 3, sm: 4, md: 3 },
        pt: 3,
        pb: 10, // footer space
        minHeight: 420,

        // ✅ keep everything visually centered but spanning full width
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Inner layout uses full width, not a fixed column */}
      <Div sx={{ width: "100%", maxWidth: "100%" }}>
        {/* Title */}
        <Typography
          sx={{
            display: "block",
            fontFamily: "Nunito, sans-serif",
            fontSize: "20px",
            fontWeight: 800,
            color: "rgb(0, 36, 101)",
            textAlign: "center",
            mb: 2,
          }}
        >
          {t("payment-modal.title")}
        </Typography>

        {/* Divider under title */}
        <Divider sx={{ mb: 3, borderColor: "rgba(0,0,0,0.08)" }} />

        {/* Promo row */}
        <Div sx={{ display: "flex", gap: 2, mb: 3, width: "100%" }}>
          <TextField
            placeholder={t("payment-modal.codePlaceHolder")}
            value={values.discountCode}
            onChange={handleChange("discountCode")}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                height: 44,
                backgroundColor: "#fff",
                fontFamily: "Nunito, sans-serif",
                fontSize: "normal",
              },
            }}
          />

          <LoadingButton
            disabled={!values.discountCode}
            loading={isLoading}
            onClick={() => {
              setErrMsg("");
              if (values.discountCode) {
                isEligibleForDiscount({
                  planId: plan.planId,
                  discountCode: values.discountCode,
                });
              }
            }}
            sx={{
              minWidth: 110,
              height: 44,
              bgcolor: "rgb(255, 137, 51)",
              color: "#fff",
              borderRadius: "10px",
              fontWeight: 550,
              fontFamily: "Nunito, sans-serif",
              "&:hover": { bgcolor: "#f57c21" },
            }}
          >
            {t("payment-modal.apply")}
          </LoadingButton>
        </Div>

        {/* Summary Card */}
        <Div
          sx={{
            width: "100%",
            backgroundColor: "#F6F7F9",
            borderRadius: "14px",
            px: { xs: 2.5, sm: 3.5 },
            py: 2.5,
          }}
        >
          <Stack spacing={1}>
            <Row
              sx={{ color: "red" }}
              label={t("payment-modal.planName")}
              value={plan.name}
            />
            <Row
              label={t("payment-modal.planPrice")}
              value={`${plan.amount} €`}
            />

            {values.applyDiscountCode?.eligible && (
              <Row
                label={t("payment-modal.discount")}
                value={
                  <span
                    style={{
                      background: "rgb(76,175,80)",
                      color: "#fff",
                      padding: "3px 10px",
                      borderRadius: 999,
                      fontSize: "13px",
                      fontWeight: 700,
                      fontFamily: "Nunito, sans-serif",
                      lineHeight: "18px",
                      display: "inline-block",
                      minWidth: 64,
                      textAlign: "center",
                    }}
                  >
                    -{values.applyDiscountCode.discountAmount} €
                  </span>
                }
              />
            )}

            <Divider sx={{ my: 0.75, borderColor: "rgba(0,0,0,0.10)" }} />

            <Div
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 540,
                  fontFamily: "Nunito, sans-serif",
                  color: "rgb(102,102,102)",
                }}
              >
                {t("payment-modal.amount")}
              </Typography>

              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 700,
                  fontFamily: "Nunito, sans-serif",
                  color: "rgb(29,106,222)",
                }}
              >
                {amountToPay} €
              </Typography>
            </Div>
          </Stack>
        </Div>

        {/* Error */}
        {errMsg && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errMsg}
          </Alert>
        )}
      </Div>

      {/* Footer buttons - centered + no left drift */}
      <Div
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 22,
          px: { xs: 3, sm: 4, md: 6 },
          display: "flex",
          gap: 2,
          justifyContent: "center",
        }}
      >
        <Button
          onClick={hide}
          sx={{
            flex: 1,
            maxWidth: 260,
            bgcolor: "rgb(224, 224, 224)",
            borderRadius: "10px",
            fontSize: "14px",
            fontWeight: 600,
            fontFamily: "Nunito, sans-serif",
            color: "rgb(102,102,102)",
            height: 44,
            "&:hover": { bgcolor: "#dbdbdb" },
          }}
        >
          {t("payment-modal.close")}
        </Button>

        <LoadingButton
          onClick={() => {
            if (values.applyDiscountCode?.eligible) {
              next({ discountCode: values.discountCode });
            } else {
              next({});
            }
          }}
          sx={{
            flex: 1,
            maxWidth: 260,
            bgcolor: "rgb(29,106,222)",
            fontSize: "14px",
            fontWeight: 600,
            fontFamily: "Nunito, sans-serif",
            color: "rgb(255,255,255)",
            borderRadius: "10px",
            height: 44,
            "&:hover": { bgcolor: "#1e4fd6" },
          }}
        >
          {t("payment-modal.Next")}
        </LoadingButton>
      </Div>
    </Div>
  );
};

export default DiscountCoupon;
